import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { filter, Subscription, switchMap, takeWhile, timer, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { format, isToday, isValid, isYesterday, parseISO } from 'date-fns';
import { AppUrls } from '../../routing/app-urls';
import { ApplicationStore, ApplicationStoreType } from '../../core/store/application.store';
import { HotelGptStore, HotelGptStoreType } from '../../core/store/hotel-gpt.store';
import { HotelGptService } from '../../hotel-gpt/service/hotel-gpt.service';
import { AgentStatus } from '../../hotel-gpt/types/agent-request';
import { FrontendHotelOption } from '../../hotel-gpt/types/agent-request';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { PopupType } from '../../shared/components/popup/popup';
import { generateRandomId } from '../../shared/utils/unique-id-utils';
import { ConfirmationPopupModel } from '../../shared/components/popup/confirmation-popup/confirmation-popup.model';

@Component({
    selector: 'juyo-conversation-history',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpinnerComponent],
    styleUrl: 'conversation-history.scss',
    template: `
        @if (!collapsed()) {
            <button class="new-chat-btn" (click)="onNewChat()">
                <span class="material-symbols-outlined">add</span>
                New Chat
            </button>
            <div class="chat-history-section">
                <div class="chat-history-header">Chat History</div>
                <div class="search-bar">
                    <span class="material-symbols-outlined search-icon">search</span>
                    <input
                        class="search-input"
                        type="text"
                        placeholder="Search..."
                        [value]="searchQuery()"
                        (input)="searchQuery.set($any($event.target).value)"
                    />
                </div>
                @if (hotelGptStore.isLoading()) {
                    <juyo-spinner class="loading-spinner" [size]="16" />
                }
                <div class="conversation-list">
                    @for (conv of filteredConversations(); track conv.conversationId) {
                        <button
                            class="conversation-item"
                            [class.active]="conv.conversationId === hotelGptStore.activeConversationId()"
                            (click)="onConversationClick(conv.conversationId)"
                        >
                            <div class="conversation-top">
                                <span class="title">{{ conv.question }}</span>
                                <span class="date">{{ formatDate(conv.updatedAt) }}</span>
                            </div>
                            <div class="conversation-bottom">
                                <span class="hotels">
                                    @if (conv.hotels.length === 1) {
                                        {{ conv.hotels[0].name }}
                                    } @else if (conv.hotels.length > 1) {
                                        <span [title]="hotelNames(conv.hotels)"
                                            >{{ conv.hotels.length }} properties</span
                                        >
                                    }
                                </span>
                                @if (hotelGptStore.pendingConversationIds().has(conv.conversationId)) {
                                    <juyo-spinner class="pending-spinner" [size]="16" />
                                } @else {
                                    <button
                                        class="delete-btn"
                                        (click)="onDeleteConversation($event, conv.conversationId)"
                                        title="Delete conversation"
                                    >
                                        <span class="material-symbols-outlined">delete</span>
                                    </button>
                                }
                            </div>
                        </button>
                    }
                </div>
            </div>
        }
    `,
})
export class ConversationHistoryComponent {
    private router = inject(Router);
    private destroyRef = inject(DestroyRef);
    private applicationStore: ApplicationStoreType = inject(ApplicationStore);
    private hotelGptService = inject(HotelGptService);
    protected hotelGptStore: HotelGptStoreType = inject(HotelGptStore);

    public collapsed = input.required<boolean>();

    protected searchQuery = signal('');
    protected filteredConversations = computed(() => {
        const q = this.searchQuery().toLowerCase().trim();
        const conversations = this.hotelGptStore.conversations();
        if (!q) return conversations;
        return conversations.filter((c) => c.question.toLowerCase().includes(q));
    });

    private pollSubscriptions = new Map<string, Subscription>();

    constructor() {
        toObservable(this.applicationStore.selectedChain)
            .pipe(
                filter((chain) => !!chain),
                switchMap((chain) => {
                    this.hotelGptStore.setIsLoading(true);
                    return this.hotelGptService.getConversations(chain!.id).pipe(catchError(() => of([])));
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((conversations) => {
                this.cancelPolling();
                this.hotelGptStore.setConversations(conversations);
                this.hotelGptStore.setIsLoading(false);
                this.hotelGptStore.startNewChat();
            });

        toObservable(this.hotelGptStore.pollJobs)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((jobs) => {
                const activeJobIds = new Set(jobs.map((j) => j.conversationId));
                for (const [convId, sub] of this.pollSubscriptions) {
                    if (!activeJobIds.has(convId)) {
                        sub.unsubscribe();
                        this.pollSubscriptions.delete(convId);
                    }
                }
                for (const job of jobs) {
                    if (!this.pollSubscriptions.has(job.conversationId)) {
                        this.startPoll(job.transactionId, job.conversationId);
                    }
                }
            });
    }

    private startPoll(transactionId: string, conversationId: string): void {
        const sub = timer(3_000, 3_000)
            .pipe(
                switchMap(() => this.hotelGptService.getStatus(transactionId)),
                takeWhile((res) => res.status === AgentStatus.Pending, true),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (response) => {
                    if (response.status === AgentStatus.Pending) return;

                    if (this.hotelGptStore.activeConversationId() === conversationId) {
                        this.hotelGptStore.updateActiveMessage(transactionId, {
                            status: response.status,
                            answer: response.answer,
                            options: response.options,
                        });
                    }

                    this.hotelGptStore.updateConversationTimestamp(conversationId);
                    this.hotelGptStore.removePendingConversation(conversationId);
                    this.hotelGptStore.removePollJob(conversationId);
                    this.pollSubscriptions.delete(conversationId);
                },
                error: () => {
                    if (this.hotelGptStore.activeConversationId() === conversationId) {
                        this.hotelGptStore.updateActiveMessage(transactionId, { status: AgentStatus.Error });
                    }

                    this.hotelGptStore.removePendingConversation(conversationId);
                    this.hotelGptStore.removePollJob(conversationId);
                    this.pollSubscriptions.delete(conversationId);
                },
            });

        this.pollSubscriptions.set(conversationId, sub);
    }

    private cancelPolling(): void {
        this.pollSubscriptions.forEach((sub) => sub.unsubscribe());
        this.pollSubscriptions.clear();
        this.hotelGptStore.clearPollJobs();
        this.hotelGptStore.clearPendingConversations();
    }

    protected onNewChat(): void {
        this.hotelGptStore.startNewChat();
        this.router.navigate(['/', AppUrls.hotelGptUrl]);
    }

    protected onConversationClick(conversationId: string): void {
        this.hotelGptStore.selectConversation(conversationId);
        this.router.navigate(['/', AppUrls.hotelGptUrl]);
    }

    protected onDeleteConversation(event: MouseEvent, conversationId: string): void {
        event.stopPropagation();

        this.applicationStore.showPopup(
            new ConfirmationPopupModel(
                generateRandomId(),
                'Delete conversation',
                'This conversation will be permanently deleted and cannot be undone.',
                () => {
                    this.hotelGptService.deleteConversation(conversationId).subscribe(() => {
                        this.hotelGptStore.removeConversation(conversationId);
                        if (this.hotelGptStore.activeConversationId() === conversationId) {
                            this.hotelGptStore.startNewChat();
                        }
                    });
                },
            ),
        );
    }

    protected hotelNames(hotels: FrontendHotelOption[]): string {
        return hotels.map((h) => h.name).join(', ');
    }

    protected formatDate(dateStr: string): string {
        if (!dateStr) {
            return '';
        }
        const date = parseISO(dateStr);
        if (!isValid(date)) {
            return dateStr;
        } else if (isToday(date)) {
            return 'Today';
        } else if (isYesterday(date)) {
            return 'Yesterday';
        } else {
            return format(date, 'MMM d');
        }
    }
}
